import uniq from 'lodash-es/uniq';
import { applyPatch, type Operation } from 'fast-json-patch';
import { type PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import cloneDeep from 'lodash-es/cloneDeep';

import type { Data } from 'ui/data/data';

import type { WorkbookTextViewState } from 'cadenza/workbook/state/workbook-text-view.state';
import { addTextViewExtraReducers, createInitialTextViewState } from 'cadenza/workbook/state/workbook-text-view.state';
import type { WorkbookConfigurationDto } from 'cadenza/model/workbook-configuration';
import { toDataViewId } from 'cadenza/workbook/model/data-view-id-utils';
import { getBaseAttributeId, isPartOfObjecttype } from 'cadenza/workbook/model/attribute-id-util';
import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import type { CadenzaStore as Store } from 'cadenza/store/cadenza-store';
import { store } from 'cadenza/store/cadenza-store';
import type { DisplayState, ExecuteCommandResult, GetWorkbookStateResult } from 'cadenza/api-client/workbook-api';
import {
  executeCommand as executeCommandRequest,
  getPartialWorkbookState,
  getWorkbookState,
  parseWorkbookUrl
} from 'cadenza/api-client/workbook-api';
import { Command, isCommandType } from 'cadenza/api-client/workbook-commands';
import { getLogger } from 'cadenza/utils/logging';
import { observeStore, STATUS } from 'cadenza/store/state-utils';
import { assert, assertNonNullable } from 'cadenza/utils/custom-error';
import { resolveObjecttypeAttributes } from 'cadenza/workbook/workbook-attribute-util';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key';
import type { QueryExecutionStrategyType } from 'cadenza/workbook/query-execution-strategy-type';
import { ViewType } from 'cadenza/workbook/api/view-type';
import { loadExtentFromSession } from 'cadenza/workbook/workbook-session-storage';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key';
import type { HexColor } from 'cadenza/utils/colors/colors';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import { WORKBOOK_ERROR_KEYS } from 'cadenza/workbook/workbook-error-keys';
import { updateWorkbookUrl } from 'cadenza/workbook/workbook-utils';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import { getDefaultSetting, getSetting, getSettingOrSettingDefault } from 'cadenza/workbook/workbook-settings-util';
import { cadenzaSessionStorage } from 'cadenza/utils/storage/storage';
import { hasCapability } from 'cadenza/data-source-util';
import {
  areAllHidden,
  conditionHasValues,
  hasValuesSet,
  getDeactivated,
  isDeactivatedForWorksheet,
  isHidden,
  isMandatory
} from 'cadenza/workbook/conditions/condition-util';
import { AttributeBundleSettingsKey } from 'cadenza/workbook/model/attribute-bundle-settings-key';
import { calculateWorksheetViewBorders } from 'cadenza/workbook/worksheet/worksheet-util';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import { isObjecttypeBound, ObjecttypeBoundState } from 'cadenza/workbook/api/objecttype';
import { compareWithLocale } from 'cadenza/utils/i18n/i18n';
import type { WorkbookIdAndHash } from 'cadenza/workbook/api/workbook-id-and-hash';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { VersionMode } from 'cadenza/repository/version-mode';
import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege';
import type { AttributeDescriptionVisibility } from 'cadenza/workbook/view/view-info/attribute-description-visibility';
import type { WorkbookDataSourceDto } from 'cadenza/workbook/model/workbook-data-source-dto';
import type { ObjecttypeIdString } from 'cadenza/repository/objecttype-id';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { ReportId } from 'cadenza/workbook-report/dto/report-id';
import type { ConditionId } from 'cadenza/workbook/model/condition-id';
import type { ConditionGroupId } from 'cadenza/workbook/model/condition-group-id';
import type { ChangesRequestDto } from 'cadenza/api-client/dto/changes-request-dto';
import { entries } from 'cadenza/utils/type-util';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { DataSourceCapability } from 'cadenza/model/data-source-capability';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { Problem } from 'cadenza/api-client/dto/problem-dto';
import type {
  ChangeCurrentExtentCommandPayload,
  CompositeCommandPayload,
  CopyViewCommandPayload,
  SetDataViewAttributeModificationCommandPayload
} from 'cadenza/api-client/workbook-command-payloads';
import type { WorkbookKey } from 'cadenza/workbook/api/workbook-key';
import type {
  FeatureInfoHeadlineType
} from 'cadenza/workbook/feature-info-settings-dialog/feature-info-headline-preview/feature-info-headline-preview';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import { validateViews } from 'cadenza/workbook/view-validation/view-validation';
import type {
  NotificationSource,
  NotificationType,
  WorkbookNotification,
  WorkbookNotificationId
} from 'cadenza/workbook/notifications/workbook-notifications';
import { isDataViewNotification, isLayerNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import { timeout } from 'cadenza/utils/timeout';
import { WorkbookSettingsKey } from 'cadenza/workbook/model/workbook-settings-key';
import { getUrlParam } from 'cadenza/utils/url-param-utils';
import type { WorkbookVersion } from 'cadenza/workbook/api/workbook-version';
import type { WorkbookType } from 'cadenza/workbook/api/workbook-type';
import type { WorkbookConfigDto } from 'cadenza/workbook/model/workbook-config-dto';
import type { BasicObjecttypeReachabilityDto } from 'cadenza/workbook/api/basic-objecttype-reachability-dto';
import type { DataProtectionDto } from 'cadenza/api-client/dto/data-protection-dto';
import type { WorkbookSelectionDto } from 'cadenza/workbook/selection/model/workbook-selection-dto';
import type { ReportDto } from 'cadenza/workbook-report/dto/report-dto';
import type { WorkbookEmbeddingTargetId } from 'cadenza/repository/model/workbook-embedding-target-id';
import type { WorkbookEmbeddingTargetDto } from 'cadenza/workbook/api/workbook-embedding-target-dto';
import type {
  ViewDrillThroughDto,
  WorksheetDrillThroughDto
} from 'cadenza/workbook/drill-through/model/drill-through-dto';
import type { ConditionToViewsRelationsDto } from 'cadenza/workbook/model/condition-to-views-relations-dto';
import type { HierarchyToViewsRelationsDto } from 'cadenza/workbook/model/hierarchy-to-views-relations-dto';
import type { ConditionGroupDto } from 'cadenza/workbook/model/condition-group-dto';
import type { HierarchyId } from 'cadenza/workbook/model/hierarchy-id';
import type { AnalysisContextHierarchyDto } from 'cadenza/workbook/model/analysis-context-hierarchy-dto';
import type { WorkbookRelation } from 'cadenza/workbook/api/workbook-relation';
import type { LoadingState } from 'cadenza/loading-state-cover/loading-state-cover';
import {
  createInitialPanelState,
  forcePanelsHidden,
  isPanelVisible,
  type PanelById,
  panelReducers,
  persistPanelState,
  updatePanelStateFromDisplayState
} from 'cadenza/workbook/state/workbook-panel.state';
import { isFeatureAvailable } from 'cadenza/features';
import type { WorkbookLiveModeState } from 'cadenza/workbook/state/workbook-live-mode.state';
import {
  createInitialLiveModeState,
  liveModeReducers,
  stopLiveModeByOther
} from 'cadenza/workbook/state/workbook-live-mode.state';
import type { MapViewWorkbookState } from 'cadenza/workbook/state/workbook-map-view.state';
import {
  createInitialMapViewState,
  mapViewReducers,
  patchMapViewState,
  patchZoomRelatedViewsState
} from 'cadenza/workbook/state/workbook-map-view.state';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key';
import { isAnyDefaultMapDefined } from 'cadenza/workbook/state/default-map.state';
import { MapDataViewSettingsKey } from 'cadenza/workbook/model/map-data-view-settings-key';
import { UNSET_OBJECTTYPE_ID } from 'cadenza/workbook/data-view/data-view-utils';

const logger = getLogger('cadenza/workbook');

export const SLICE_PREFIX = 'workbookSlice'; // exported for testing

const WORKBOOK_CHANGED_KEY = 'workbook-changed';
const PARAMETER_MAXIMIZED_VIEW_ID = 'maximizedViewId';

const parsedWorkbookUrl = parseWorkbookUrl(location.pathname)!;

/**
 * Enum for the changed state of the workbook.
 */
type ChangedState = 'no' | 'minor' | 'major';

export type ViewMetadata = Record<string, unknown>;

export type ViewWithMetadata = ViewDto & { metadata: ViewMetadata };

export type WorkbookState = WorkbookLiveModeState & MapViewWorkbookState & WorkbookTextViewState & {
  workbookConfiguration?: WorkbookConfigurationDto;
  /** `WorkbookIdAndHash` of the current workbook */
  workbookIdAndHash: WorkbookIdAndHash;
  /** The ID of the active (currently shown) worksheet */
  activeWorksheetId: WorksheetId;
  /** The ID of the active view or '' if no view is active */
  activeViewId?: ViewId;
  /** The ID of the maximized view or '' if no view is maximized */
  maximizedViewId: ViewId | '';
  /** workbook repository **/
  repository?: RepositoryDto;
  /** The print name of the workbook */
  name: string;
  /** The version of the workbook (undefined if the workbook is unsaved) */
  version?: WorkbookVersion;
  /** The version mode of the workbook */
  versionMode?: VersionMode;
  /** Whether the workbook is saved (tri-state!) */
  saved: boolean;
  /**
   * Whether the workbook is changed, i.e. whether a command was executed since it was last saved.
   *
   * _Note:_ An unsaved workbook is considered changed.
   */
  changed: ChangedState;
  /**
   * Whether the workbook was changed on the client
   *
   * _Note:_ This is a hack for changes that are not caused by a command execution
   * but should still be persisted on save.
   */
  clientChanged: boolean;
  /** Privileges of the acting user on the workbook */
  privileges: ObjectPrivilege[];
  /** Type of the workbook */
  workbookType: WorkbookType;
  /** The workbook's settings */
  settings: Settings;
  /** The workbook config from `workbook-config.xml` */
  config?: WorkbookConfigDto;
  /** The status of the initial workbook state fetching (see `fetchInitialState()`) */
  initializationStatus: STATUS;
  /** The status of the current command execution (see `executeCommand()`) or 'pending' if no command is being executed */
  commandStatus: STATUS;
  /**
   * The error key of the previously executed command error '' if there was no error
   *
   * Used to show specific messages for certain errors.
   *
   * @see problemType in WorkbookExceptionHandler.java
   */
  commandErrorKey: string;
  /** The error detail of the previously executed command error (if any) */
  commandErrorDetail?: unknown;
  /** Datasources of the objecttypes that were added to the workbook */
  datasources: WorkbookDataSourceDto[];
  /** Objecttypes that were added to the workbook by ID */
  objecttypeById: Record<ObjecttypeIdString, Objecttype>;
  /** list of unavailable objecttypes that are still added to the workbook (e.g. deleted from repository) */
  unavailableObjecttypeIds: ObjecttypeIdString[];
  /** For each base OT, the other OTs that are reachable, including cardinality of the associations */
  reachableObjecttypesByBasicObjecttypeId: Record<ObjecttypeIdString, BasicObjecttypeReachabilityDto>;
  /** Data protection of the workbook */
  dataProtection?: DataProtectionDto;
  /** Attributes of the objecttypes that were added to the workbook by ID */
  attributeById: Record<AttributeId, WorkbookAttributeDto>;
  /** Attributes that are active (used) in one of the workbook's data views by (active!) ID */
  activeAttributeById: Record<ActiveAttributeId, WorkbookAttributeDto>;
  /** The current dimension/measure value selection in the workbook or undefined if there's no selection */
  selection?: WorkbookSelectionDto;
  /** The IDs of the worksheets of the workbook (in order) */
  worksheets: WorksheetId[];
  /** The worksheets of the workbook by ID */
  worksheetById: Record<WorksheetId, WorksheetDto>;
  /** The views of the workbook by ID */
  viewById: Record<ViewId, ViewDto>;
  /**
   * Metadata of the views of the workbook by ID
   *
   * _Note:_ These client-side metadata are currently separate from the actual views
   * so that they survive view updates from the server.
   */
  viewMetadataById: Record<ViewId, ViewMetadata>;
  /** IDs of views and data views that need to be refreshed */
  invalidViewsOrDataViews: (ViewId | DataViewId)[];
  /** IDs of layers by view id that needs to be refreshed */
  invalidLayerIdsByViewId: Record<ViewId, LayerId[]>;
  /** IDs of data views that have an outdated selection state */
  dataViewsAffectedBySelectionChange: DataViewId[];
  /** The data views of the workbook by ID */
  dataViewById: Record<DataViewId, DataViewDto>;
  /** The current notifications for data views, by view ID */
  notificationsByView: Record<ViewId, Record<WorkbookNotificationId, WorkbookNotification>>;
  /** The curated reports of the workbook By ID */
  reportById: Record<ReportId, ReportDto>;
  /** The embedding targets of the workbook by ID */
  embeddingTargetById: Record<WorkbookEmbeddingTargetId, WorkbookEmbeddingTargetDto>;
  /**
   * Workbook specific drill-through definitions
   * (not including the legacy drill-throughs defined via Cadenza Desktop)
   */
  drillThroughTargets: (WorksheetDrillThroughDto | ViewDrillThroughDto)[];
  /** List of condition IDs and condition group IDs that defines the order within the global analysis context */
  conditionsAndGroups: (ConditionId | ConditionGroupId)[];
  /** All conditions of the workbook by ID (global and locals ones) */
  conditionById: Record<ConditionId, ConditionDto>;
  /** Views and data views affected by a condition by condition ID */
  affectedViewsByConditionId: Record<ConditionId, ConditionToViewsRelationsDto>;
  /** Views and data views affected by a hierarchy by condition ID */
  affectedViewsByHierarchyId: Record<HierarchyId, HierarchyToViewsRelationsDto>;
  /** Conditions groups of the workbook by ID (Always global, we don't have local condition groups.) */
  conditionGroupById: Record<ConditionGroupId, ConditionGroupDto>;
  /** Hierarchies that are controlled in the analysis context by ID */
  hierarchyById: Record<HierarchyId, AnalysisContextHierarchyDto>;
  /**
   * The workbook panel states by panel ID
   *
   * Panels are visible and not disabled by default.
   */
  panelById: PanelById;
  /** true if the workbook state is used from within a PDF report */
  reportMode: boolean;
  forceMaximizePreviousState?: { maximizedViewId: ViewId | ''; panelById: PanelById };
  displayState?: DisplayState;
  relation?: WorkbookRelation;
  isOnDemandWorksheetLoading: boolean;
  /** A map containing which on demand layers from a view are being loaded. A layer is being loaded when the user clicked the "Update" button */
  onDemandLayersLoading: Record<ViewId, LayerId[]>;
  /** ID of the layer which settings should be displayed in the designer, will be reset after first display */
  activeLayerId?: LayerId;

  /* Need to be in store, because we need to have separate one for store and secondaryStore */
  lastCommand?: Command;
};

// Note: If you add properties here which are not coming from the backend, please take a look at the
// fetchInitialState reducer in order not to overwrite properties.
// Note: It's not a static object, but function because initial state depends on what we have in
// session storage. When we use static object then in fetchInitialState we override the state wrongly
function createInitialState (): WorkbookState {
  return {
    workbookConfiguration: getWorkbookConfigFromPageData(),
    workbookIdAndHash: parsedWorkbookUrl?.workbookIdAndHash ?? {}, // fallback for testing
    activeWorksheetId: parsedWorkbookUrl?.worksheetId,
    maximizedViewId: getInitialMaximizedViewIdFromUrlParam(PARAMETER_MAXIMIZED_VIEW_ID),
    name: '',
    saved: false,
    changed: 'major',
    clientChanged: cadenzaSessionStorage.getItem(WORKBOOK_CHANGED_KEY) === parsedWorkbookUrl?.workbookIdAndHash.id,
    privileges: [],
    workbookType: 'WORKBOOK',
    settings: {},
    initializationStatus: STATUS.PENDING,
    commandStatus: STATUS.PENDING,
    commandErrorKey: '',
    datasources: [],
    objecttypeById: {},
    reachableObjecttypesByBasicObjecttypeId: {},
    unavailableObjecttypeIds: [],
    attributeById: {},
    activeAttributeById: {},
    worksheets: [],
    worksheetById: {},
    viewById: {},
    viewMetadataById: {},
    invalidViewsOrDataViews: [],
    invalidLayerIdsByViewId: {},
    dataViewsAffectedBySelectionChange: [],
    dataViewById: {},
    notificationsByView: {},
    reportById: {},
    embeddingTargetById: {},
    drillThroughTargets: [],
    conditionsAndGroups: [],
    conditionById: {},
    affectedViewsByConditionId: {},
    affectedViewsByHierarchyId: {},
    conditionGroupById: {},
    hierarchyById: {},
    reportMode: false,
    isOnDemandWorksheetLoading: false,
    onDemandLayersLoading: {},
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    repository: {} as RepositoryDto, // for testing
    ...createInitialPanelState(),
    ...createInitialLiveModeState(),
    ...createInitialMapViewState(),
    ...createInitialTextViewState()
  };
}

export const reloadObjecttype = async (objecttypeId: ObjecttypeIdString) => {
  const fetchInitial = store.dispatch(fetchInitialState());
  // invalidate dataViews explicitly as map dataViews wouldn't get refreshed due to special caching logic
  const invalidateDataViews = store.dispatch(invalidateDataViewsUsingObjecttype(objecttypeId));
  return Promise.allSettled([ fetchInitial, invalidateDataViews ]);
};

/**
 * Creates an async thunk for fetching the initial workbook state.
 *
 * @return A command result (reusing the DTO from the command execution)
 */
export const fetchInitialState = createAsyncThunk(
  SLICE_PREFIX + '/fetchInitialState',
  // do not catch the error here: it would result in an infinite loading spinner
  // as the caller relies on the "rejected" state of redux as this is executed async.
  (arg: {
    workbookIdAndHash?: WorkbookIdAndHash;
    secondaryStoreMerge?: boolean;
  } | undefined, thunkAPI) => {
    const data = document.querySelector<Data<GetWorkbookStateResult>>('#initial-workbook-state');
    if (data) {
      data.remove();
      return Promise.resolve(data.value);
    }
    return getWorkbookState(arg?.workbookIdAndHash ?? slice(thunkAPI.getState()).workbookIdAndHash);
  });

export const resetState = createAsyncThunk(
  SLICE_PREFIX + '/resetState',
  () => {
  });

/**
 * Creates an async thunk for fetching a partial workbook state. Only items requested in the provided changesRequestDto will be requested from backend and updated in the workbook state.
 * Note that current implementation does not invalidate any view/dataView.
 * In case you need this for your use case, ensure that the implementation in the backed supports it.
 */
export const fetchPartialState = createAsyncThunk(SLICE_PREFIX + '/fetchPartialState', (changesRequestDto: Partial<ChangesRequestDto>, thunkAPI) => {
  const state = slice(thunkAPI.getState());
  return getPartialWorkbookState(state.workbookIdAndHash, {
    objecttypeIds: changesRequestDto.objecttypeIds ?? [],
    updateDataProtection: changesRequestDto.updateDataProtection ?? false
  });
});

interface RestoreStateMarker {
  workbookIdAndHash: WorkbookIdAndHash;
  changed: ChangedState;
  clientChanged: boolean;
}

export const restoreState = createAsyncThunk(SLICE_PREFIX + '/restoreState', (marker: RestoreStateMarker) => {
  const { workbookIdAndHash } = marker;
  // do not catch the error here: it would result in an infinite loading spinner as the caller relies on the "rejected" state of redux as this is executed async
  return getWorkbookState(workbookIdAndHash);
});

let currentCommandPromise: Promise<ExecuteCommandResult> | undefined;

/**
 * Creates an async thunk for executing a command.
 *
 * Only one command is executed at a time.
 *
 * @param command - The command to be executed
 * @return The command result
 */
export const executeCommand = createAsyncThunk(SLICE_PREFIX + '/executeCommand', (command: Command, thunkAPI) => {
  logger.log('Schedule command', command);

  /*
   * Promise.prototype.finally() won't work, because the return value of the final callback
   * is ignored, i.e. Promise.resolve(1).finally(() => 2) will be resolved with 1.
   * See https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Promise/finally
   */
  const promise = Promise.allSettled([ currentCommandPromise ])
    .then(() => {
      const state = slice(thunkAPI.getState());
      logger.info('Execute command', command);
      return executeCommandRequest(state.workbookIdAndHash, command);
    })
    .catch(error => {
      logger.error('Failed to execute command', command, error);
      // In order to pass the error to the `rejected` callback, we need to use this Redux method
      // instead of just throwing the error here.
      return thunkAPI.rejectWithValue(error);
    })
    .finally(() => {
      currentCommandPromise = undefined;
    }) as Promise<ExecuteCommandResult>;

  // currentCommandPromise is used to block the next commands from executing until the first
  // command is fulfilled (including store update). Since redux will write the data into store
  // after we return from this method (payloadCreator), we need to ensure that we fulfill the
  // currentCommandPromise afterward. That's why we need an additional timeout() here.
  currentCommandPromise = promise.then(result => {
    logger.log('Command executed', command, result);
    return timeout();
  });

  return promise;
});

function setStateFromUpdate (state: WorkbookState, action: PayloadAction<GetWorkbookStateResult, string, {
  arg: unknown;
}>) {
  const {
    operations,
    invalidViewsOrDataViews,
    invalidLayerIdsByViewId,
    dataViewsAffectedBySelectionChange,
    version,
    versionMode
  } = action.payload;
  Object.assign(state,
    updateWorkbookState(state, { patch: operations }),
    { version, versionMode });
  updatePanelStateFromDisplayState(state);
  state.clientChanged = false;

  state.unavailableObjecttypeIds = action.payload.unavailableObjecttypeIds ?? [];
  updateDataViewInvalidationState(state, invalidViewsOrDataViews, invalidLayerIdsByViewId, dataViewsAffectedBySelectionChange);

  if (action.type === SLICE_PREFIX + '/restoreState/fulfilled') {
    const { workbookIdAndHash, changed, clientChanged } = action.meta.arg as RestoreStateMarker;
    state.workbookIdAndHash = workbookIdAndHash;
    state.changed = changed;
    state.clientChanged = clientChanged;
    state.lastCommand = undefined;
  }
}

function resetToEmpty (state: WorkbookState) {
  Object.assign(state, createInitialState());
}

function doAddNotifications (
  state: WorkbookState,
  viewId: ViewId,
  added: WorkbookNotification[]
) {
  const viewNotifications = getNotificationsForViewByNotificationId(state, viewId);
  added.forEach(notification => (viewNotifications[notification.id] = notification));
}

function getWorkbookConfigFromPageData () {
  return document.querySelector<Data<WorkbookConfigurationDto>>('#config-workbook')?.value;
}

export const workbookSlice = createSlice({
  name: SLICE_PREFIX,
  // Make a copy because we'll reuse it when we want to reset state, and if not, it will be patched by immer and made immutable
  initialState: createInitialState(),
  reducers: {
    // Should be a thunk, but too complicated for now
    updateWorkbookMetadata (state, {
      payload: {
        workbookIdAndHash = state.workbookIdAndHash,
        name = state.name,
        version = state.version,
        versionMode = state.versionMode,
        saved = state.saved,
        changed = state.changed,
        clientChanged = state.clientChanged,
        privileges = state.privileges,
        workbookType = state.workbookType,
        activeWorksheetId = state.activeWorksheetId,
        reportMode = state.reportMode
      }
    }: PayloadAction<Partial<WorkbookState>>) {
      if (saved && state.saved) {
        changed = 'no';
        clientChanged = false;
      }
      Object.assign(state, {
        workbookIdAndHash,
        name,
        saved,
        changed,
        clientChanged,
        version,
        versionMode,
        privileges,
        workbookType,
        activeWorksheetId,
        reportMode
      });
    },
    updateWorkbookChanged (state, {
      payload: {
        changed = state.changed,
        clientChanged = state.clientChanged
      }
    }) {
      Object.assign(state, {
        changed,
        clientChanged
      });
    },
    updateViewMetadata: {
      prepare: (viewId: ViewId, metadata: ViewMetadata) => ({ payload: { viewId, metadata } }),
      reducer: (state, { payload: { viewId, metadata } }: PayloadAction<{
        viewId: ViewId;
        metadata: ViewMetadata;
      }>) => {
        let viewMetadata = state.viewMetadataById[viewId];
        if (!viewMetadata) {
          viewMetadata = {};
          state.viewMetadataById[viewId] = viewMetadata;
        }
        Object.entries(metadata).forEach(([ key, value ]) => {
          if (value !== undefined) {
            viewMetadata[key] = value;
          } else {
            delete viewMetadata[key];
          }
        });
      }
    },
    resetViewMetadata (state, { payload }) {
      delete state.viewMetadataById[payload];
    },
    removeObjectTypes (state, { payload }) {
      Object.assign(state, removeObjecttypesById(state, payload));
    },
    ...panelReducers,
    ...liveModeReducers,
    ...mapViewReducers,
    activateView (state, { payload: viewId }) {
      activateViewInternally(state, viewId);
    },
    maximizeView (state, { payload: viewId }) {
      state.maximizedViewId = viewId;
    },
    forceMaximizeView (state, { payload: viewId }: { payload: ViewId | undefined }) {
      if (viewId) {
        state.forceMaximizePreviousState = {
          maximizedViewId: state.maximizedViewId,
          // cloneDeep() cannot be replaced with structuredClone() in this case,
          // because that fails to clone the Immer proxies.
          panelById: cloneDeep(state.panelById)
        };

        state.maximizedViewId = viewId;
        forcePanelsHidden(state);
      } else {
        Object.assign(state, state.forceMaximizePreviousState);
        delete state.forceMaximizePreviousState;
      }
    },
    initializeWorksheet (state) {
      activateOnlyViewIfAvailable(state);
    },
    unmarkViewAsDirty (state, { payload: viewId }) {
      const view = getViewById(state, viewId);
      const dataViewIds = view.dataViews;

      state.invalidViewsOrDataViews = state.invalidViewsOrDataViews
        .filter(viewIdOrDataViewId => view.id !== viewIdOrDataViewId
          && !dataViewIds.includes(viewIdOrDataViewId as DataViewId));
      state.dataViewsAffectedBySelectionChange = state.dataViewsAffectedBySelectionChange
        .filter(dataViewId => !dataViewIds.includes(dataViewId));
    },
    invalidateDataView (state, { payload: dataViewId }) {
      if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
        state.invalidViewsOrDataViews.push(dataViewId);
      }
    },
    invalidateDataViewsUsingObjecttypes (state, { payload: objecttypeIds }) {
      const dataViewIdsUsingObjecttype = getDataViewIdsUsingObjecttypes(state, objecttypeIds);

      for (const dataViewId of dataViewIdsUsingObjecttype) {
        if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
          state.invalidViewsOrDataViews.push(dataViewId);
        }
      }
    },
    invalidateDataViewsUsingObjecttype (state, { payload: objecttypeId }) {
      const dataViewIdsUsingObjecttype = getDataViewIdsUsingObjecttype(state, objecttypeId);

      for (const dataViewId of dataViewIdsUsingObjecttype) {
        if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
          state.invalidViewsOrDataViews.push(dataViewId);
        }
      }
    },
    updateObjecttypeBoundState (state, { payload: { objecttypeId, boundState } }) {
      const objecttype = slice(state).objecttypeById[objecttypeId];
      objecttype.boundState = boundState;
    },
    updateObjecttypeBoundStateOnSave (state) {
      Object.values(slice(state).objecttypeById).forEach(objecttype => {
        if (objecttype.boundState === ObjecttypeBoundState.TRANSIENT) {
          objecttype.boundState = ObjecttypeBoundState.BOUND_TO_WORKBOOK;
        }
      });
    },
    updateWorkbookName (state, { payload }) {
      state.name = payload;
    },
    removeNotifications (
      state: WorkbookState,
      { payload: { viewId, removedIds } }:
      { payload: { viewId: ViewId; removedIds: WorkbookNotificationId[] } }
    ) {
      const viewNotifications = getNotificationsForViewByNotificationId(state, viewId);
      removedIds.forEach(removedId => delete viewNotifications[removedId]);
    },
    removeNotificationsBySource (
      state: WorkbookState,
      { payload: { viewId, source } }:
      { payload: { viewId: ViewId; source: NotificationSource } }
    ) {
      const notificationsById = state.notificationsByView[viewId] || {};
      Object.values(notificationsById).forEach(notification => {
        if (notification.source === source) {
          delete notificationsById[notification.id];
        }
      });
    },
    addNotifications (
      state: WorkbookState,
      { payload: { viewId, added } }:
      { payload: { viewId: ViewId; added: WorkbookNotification[] } }
    ) {
      doAddNotifications(state, viewId, added);
    },
    resetActiveLayerId (state) {
      delete state.activeLayerId;
    },
    removeUnusedObjecttypeIds (state: WorkbookState, { payload: { objecttypeIds } }) {
      state.unavailableObjecttypeIds = state.unavailableObjecttypeIds.filter(u => !objecttypeIds.includes(u));
    },
    loadOnDemandWorksheet (state: WorkbookState) {
      state.isOnDemandWorksheetLoading = true;
    },
    loadOnDemandLayers (state: WorkbookState, { payload: viewId }: { payload: ViewId }) {
      state.onDemandLayersLoading[viewId] = getOnDemandLayersIds(state, viewId);
    },
    onDemandLayerFinishedLoading (
      state: WorkbookState,
      { payload: { layerId, viewId } }: { payload: { layerId: LayerId; viewId: ViewId } }
    ) {
      state.onDemandLayersLoading[viewId] = state.onDemandLayersLoading[viewId]?.filter((id) => id !== layerId) ?? [];
    }
  },
  // The "pending", "fulfilled" and "rejected" actions are dispatched automatically.
  // Hence, the reducers go in a separate structure.
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitialState.pending, (state) => {
        state.initializationStatus = STATUS.PENDING;
      })
      .addCase(fetchInitialState.fulfilled, (state, action) => {
        // We reinitialize the state to the initial state and set the current workbookIdAndHash on top
        // (we don't want the initial workbookIdAndHash), this is necessary when the Save as actions is triggered
        // from a workbook because we really want to reinitialize the store from scratch
        Object.assign(state, createInitialState(), {
          workbookIdAndHash: action.payload.workbookIdAndHash,
          activeWorksheetId: state.activeWorksheetId,
          // The following properties are not coming from the backend, so we don't want to overwrite them
          name: state.name,
          clientChanged: state.clientChanged,
          saved: state.saved,
          changed: state.changed,
          maximizedViewId: state.maximizedViewId,
          viewMetadataById: state.viewMetadataById
        });
        state.initializationStatus = STATUS.SUCCEEDED;
        if (action.meta.arg?.secondaryStoreMerge) {
          state.lastCommand = undefined;
        }
        setStateFromUpdate(state, action);
        patchZoomRelatedViewsState(state, action.payload);
      })
      .addCase(fetchInitialState.rejected, (state) => {
        state.initializationStatus = STATUS.FAILED;
      })
      .addCase(fetchPartialState.pending, (state) => {
        state.commandStatus = STATUS.PENDING;
      })
      .addCase(fetchPartialState.fulfilled, (state, action) => {
        state.commandStatus = STATUS.SUCCEEDED;
        const {
          workbookIdAndHash,
          operations
        } = action.payload;
        Object.assign(state, updateWorkbookState(state, { patch: operations }), { workbookIdAndHash });
        state.commandErrorKey = '';
        state.commandErrorDetail = null;
      })
      .addCase(fetchPartialState.rejected, (state) => {
        state.commandStatus = STATUS.FAILED;
      })
      .addCase(restoreState.fulfilled, (state, action) => {
        setStateFromUpdate(state, action);
      })
      .addCase(restoreState.rejected, () => {
        logger.error('Could not restore redux state!');
      })
      .addCase(resetState.fulfilled, (state) => {
        resetToEmpty(state);
      })
      .addCase(resetState.rejected, () => {
        logger.error('Could not restore redux state!');
      })
      .addCase(executeCommand.pending, (state, action) => {
        state.commandStatus = STATUS.PENDING;
        // Whenever a command is executed, stop live mode.
        stopLiveModeByOther(state);
        if (isActiveWorksheetSettingOnDemand(state)) {
          const command = action.meta.arg;
          const isOnDemandRelevantCommand = shouldOnDemandBeConsidered(state, command);
          state.isOnDemandWorksheetLoading = !isOnDemandRelevantCommand;
        }
      })
      .addCase(executeCommand.fulfilled, (state, action) => {
        state.commandStatus = STATUS.SUCCEEDED;
        // cloneDeep() cannot be replaced with structuredClone() in this case,
        // because that fails to clone the Immer proxies.
        const previousActiveViewState = cloneDeep(getActiveView(state));
        const {
          workbookIdAndHash,
          operations,
          invalidViewsOrDataViews,
          invalidLayerIdsByViewId,
          dataViewsAffectedBySelectionChange
        } = action.payload;
        Object.assign(state, updateWorkbookState(state, {
          patch: operations
        }), { workbookIdAndHash });
        state.lastCommand = action.meta.arg;
        updateDataViewInvalidationState(state, invalidViewsOrDataViews, invalidLayerIdsByViewId, dataViewsAffectedBySelectionChange);
        updateActiveLayerId(state, previousActiveViewState, state.lastCommand!);
        unsetMaximizedViewIfViewDoesNotExist(state, state.lastCommand!);

        const viewIds = new Set(Object.keys(state.viewById));
        state.viewMetadataById = Object.fromEntries(
          Object.entries(state.viewMetadataById).filter(([ viewId ]) => viewIds.has(viewId)));

        patchMapViewState(state, action.payload);

        state.commandErrorKey = '';
        state.commandErrorDetail = null;
      })
      .addCase(executeCommand.rejected, (state, action) => {
        const problem = action.payload as Problem | undefined;
        // if the actual error is swallowed (our default) then the `payload` is `undefined`
        state.commandErrorKey = problem?.key || '';
        state.commandErrorDetail = createCommandErrorDetails(problem);
        state.commandStatus = STATUS.FAILED;
      });
    addTextViewExtraReducers(builder);
  }
});

/**
 * Updates the given workbook state with a patch (command response) from the server.
 *
 * @param state - The state object to update
 * @param context
 * @param context.patch - A JSON patch
 * @return The updated state object
 */
function updateWorkbookState (state: WorkbookState, { patch }: { patch: Operation[] }) {
  const nextState = applyPatch(state, patch).newDocument;
  const activeWorksheet = getActiveWorksheet(state);
  if (!activeWorksheet || isWorksheetOnDemand(activeWorksheet)
    && shouldOnDemandBeConsidered(state, slice(state).lastCommand)
    && hasDirtyViews(nextState)) {
    state.activeViewId = undefined;
  } else {
    activateOnlyViewIfAvailable(state);
  }
  validateViews(nextState);
  return nextState;
}

function hasDirtyViews (nextState: WorkbookState) {
  return nextState.invalidViewsOrDataViews.length > 0;
}

/**
 * Activates the only view of the active worksheet if the designer is visible.
 */
function activateOnlyViewIfAvailable (state: WorkbookState) {
  const views = getActiveViewsOfWorksheet(state);
  if (isPanelVisible(state, 'd-view-settings-panel') && views.length === 1) {
    state.activeViewId = views[0].id;
  }
}

function removeObjecttypesById (
  {
    objecttypeById,
    attributeById,
    datasources
  }: Pick<WorkbookState, 'objecttypeById' | 'attributeById' | 'datasources'>,
  objecttypeIdsToRemove: ObjecttypeIdString[]
) {
  assert(Array.isArray(objecttypeIdsToRemove), 'objecttypeIdsToRemove has to be an array');
  // remove object types and their attributes
  for (const objecttypeId of objecttypeIdsToRemove) {
    if (!objecttypeById[objecttypeId]) {
      continue;
    }
    const attributeIds = objecttypeById[objecttypeId].attributes;
    for (const attributeId of attributeIds) {
      delete attributeById[attributeId];
    }
    // If we removed enrichment object type we have to do cleanup.
    // Code below removes orphan references to deleted enrichment stored in enriched objecttypes enrichment array.
    const enrichments = objecttypeById[objecttypeId].enrichments;
    if (enrichments) {
      const enrichmentIds = enrichments.map(enrichment => enrichment.objecttypeId);
      removeObjecttypesById({ objecttypeById, attributeById, datasources }, enrichmentIds);
    } else {
      // this removes all the references
      for (const objecttype of Object.values(objecttypeById)) {
        if (objecttype.enrichments) {
          objecttype.enrichments = objecttype.enrichments.filter(enrichment => !objecttypeIdsToRemove.includes(enrichment.objecttypeId));
        }
      }
    }
    delete objecttypeById[objecttypeId];
  }

  // remove obsolete datasources
  const uniqueDataSourceIds = new Set(
    Object.values(objecttypeById).map(({ dataSourceId }) => dataSourceId)
  );
  const newDataSources = datasources.filter(({ name }) => uniqueDataSourceIds.has(name));

  return { objecttypeById, attributeById, datasources: newDataSources };
}

const { reducer, actions } = workbookSlice;
store.registerReducer(SLICE_PREFIX, reducer);

// Export reducer for testing
export { reducer };

// Action creators

export const {
  updateWorkbookMetadata,
  updateWorkbookChanged,
  updateViewMetadata,
  resetViewMetadata,
  removeObjectTypes,
  togglePanelVisibility,
  hideAllPanels,
  activateView,
  maximizeView,
  forceMaximizeView,
  initializeWorksheet,
  unmarkViewAsDirty,
  invalidateDataView,
  invalidateDataViewsUsingObjecttype,
  invalidateDataViewsUsingObjecttypes,
  updateObjecttypeBoundState,
  updateObjecttypeBoundStateOnSave,
  updateWorkbookName,
  removeNotifications,
  removeNotificationsBySource,
  addNotifications,
  resetActiveLayerId,
  removeUnusedObjecttypeIds,
  refreshViewData,
  startLiveMode,
  stopLiveMode,
  removeZoomRelatedViewId,
  setTargetMapExtentAndScaleForCoupledView,
  synchronizeToLeftTopMostView,
  stopInteractionForCoupledView,
  loadOnDemandWorksheet,
  loadOnDemandLayers,
  onDemandLayerFinishedLoading
} = actions;

export const deactivateView = () => activateView('');
export const minimizeView = () => maximizeView('');

// Selectors

export const isWorkbookActive = (state: WorkbookState) => !!slice(state).workbookIdAndHash;

export const getWorkbookConfiguration = (state: WorkbookState) => slice(state).workbookConfiguration;
export const getCouplingLayerLimit = (state: WorkbookState) => slice(state).workbookConfiguration!.restrictions.couplingLayerLimit;
export const getInitializationStatus = (state: WorkbookState) => slice(state).initializationStatus;
export const getCommandStatus = (state: WorkbookState) => slice(state).commandStatus;

export const getWorkbookIdAndHash = (state: WorkbookState) => slice(state).workbookIdAndHash;
export const getWorkbookVersion = (state: WorkbookState) => slice(state).version;
/**
 * Returns workbook version mode.
 * It may be a mode coming from the backend (if the repository has publishing process enabled and there version is marked as 'published' or 'development')
 * or calculated here.
 */
export const getWorkbookVersionMode = (state: WorkbookState): VersionMode => {
  const publishingProcessEnabled = isRepositoryPublishingProcessEnabled(state);
  const saved = isWorkbookSaved(state);
  if (!publishingProcessEnabled) {
    return saved ? 'saved' : 'unsaved';
  }
  // set by the backend?
  const savedVersionMode = slice(state).versionMode;
  if (savedVersionMode) {
    return savedVersionMode;
  }
  // a saved workbook with publishingProcess is a draft
  return saved ? 'development' : 'unsaved';
};
export const getActiveWorksheetId = (state: WorkbookState) => slice(state).activeWorksheetId!;
export const getWorksheetById = (state: WorkbookState, worksheetId: WorksheetId) => slice(state).worksheetById[worksheetId];

export const isRepositoryPublishingProcessEnabled = (state: WorkbookState) => slice(state).repository?.publishingProcessEnabled ?? false;

export const isWorkbookDeletionDeadlineEnabled = (state: WorkbookState) => slice(state).repository?.deletionDeadlineWorkbookEnabled ?? false;

/**
 * Returns all worksheets of the workbook.
 */
export const getWorksheets = (state: WorkbookState) => {
  const worksheetIds = slice(state).worksheets;
  return worksheetIds.map((id) => getWorksheetById(state, id));
};

export const getFirstWorksheet = (state: WorkbookState) => getWorksheets(state)[0];

/**
 * <p>
 *     <b>Important note</b><br/>
 *     Be aware that the concept of active worksheet does not exist in reporting, the
 *     method will return <code>undefined</code>. Consider this in direct calls as well
 *     as selectors that make use of this. It is safer to use {@link getWorksheetByViewId}
 *     if you have access to the viewId.
 * </p>
 * @param state
 */
export const getActiveWorksheet = (state: WorkbookState) => getWorksheetById(state, getActiveWorksheetId(state));
export const isActiveWorksheetOnDemand = (state: WorkbookState) => {
  return isActiveWorksheetSettingOnDemand(state) && !slice(state).isOnDemandWorksheetLoading && !isReportMode(state);
};
export const getWorksheetByViewId = (state: WorkbookState, viewId: ViewId) => getWorksheets(state)
  .find(worksheet => worksheet.views.includes(viewId));

export const getWorkbookRepositoryName = (state: WorkbookState) => slice(state).repository!.name;
export const workbookRepositoryHasCmdb = (state: WorkbookState) => slice(state).repository?.cadenzaManagedDB ?? false;
export const getWorkbookRepository = (state: WorkbookState) => slice(state).repository;
export const getWorkbookKey = (state: WorkbookState): WorkbookKey => ({
  repositoryName: slice(state).repository!.name,
  id: slice(state).workbookIdAndHash.id
});
export const getWorkbookPrivileges = (state: WorkbookState) => slice(state).privileges;
export const getWorkbookName = (state: WorkbookState) => slice(state).name;
export const getWorkbookType = (state: WorkbookState) => slice(state).workbookType;
export const getWorkbookRelation = (state: WorkbookState) => slice(state).relation;
export const isWorkbookSaved = (state: WorkbookState) => slice(state).saved;
export const getChangedState = (state: WorkbookState) => slice(state).changed;
export const isClientChanged = (state: WorkbookState) => slice(state).clientChanged;
export const isWorkbookChanged = (state: WorkbookState) => slice(state).changed !== 'no' || slice(state).clientChanged;
export const hasMajorChange = (state: WorkbookState) => slice(state).changed === 'major';
export const isWorkbookVisible = (state: WorkbookState) => hasPrivilege(slice(state).privileges, 'visible');
export const isWorkbookSharable = (state: WorkbookState) => hasPrivilege(slice(state).privileges, 'share');
export const isWorkbookWritable = (state: WorkbookState) => hasPrivilege(slice(state).privileges, 'write');
export const isWorkbookVisibilityRestricted = (state: WorkbookState) => slice(state).saved && isVisibilityRestricted(slice(state).privileges);
export const isWorkbookDesignerEnabled = (state: WorkbookState) => slice(state).settings[WorkbookSettingsKey.toPersistenceKey('WORKBOOK_SIMPLIFIED_DESIGN_ENABLED')] !== 'false';
export const isWorkbookViewLayoutEditable = (state: WorkbookState) => slice(state).settings[WorkbookSettingsKey.toPersistenceKey('WORKBOOK_SIMPLIFIED_VIEW_LAYOUT_ENABLED')] !== 'false';
export const isWorkbookDataViewContentEditable = (state: WorkbookState) => slice(state).settings[WorkbookSettingsKey.toPersistenceKey('WORKBOOK_SIMPLIFIED_DATA_VIEW_CONTENT_ENABLED')] !== 'false';
export const getAttributeDescriptionVisibility = (state: WorkbookState): AttributeDescriptionVisibility => slice(state).config!.attributeDescriptionVisibility;
export const areRequiredConditionsValid = createSelector(getActiveWorksheet, worksheet => worksheet.requiredConditionsValid);

/**
 * Checks if the workbook is empty.
 * _Note_: Here, empty refers to having no views, no data sources, no object types and only one worksheet.
 * Workbook settings like drillthrough targets, the worksheet names etc. are not taken into account here.
 */
export const isWorkbookEmpty = (state: WorkbookState) =>
  (getViewIds(state).length === 0)
  && (getDataSources(state).length === 0)
  && (getObjecttypes(state).length === 0)
  && (getWorksheets(state).length === 1);

/**
 * Returns the workbook specific drill-through targets (not including the legacy targets
 * defined via Cadenza Desktop)
 */
export const getDrillThroughTargets = (state: WorkbookState) => slice(state).drillThroughTargets;

/**
 * Returns all curated reports
 *
 * @param state - The state object
 * @return All reports
 */
export const getReports = createSelector((state: WorkbookState) => slice(state).reportById, Object.values);

/**
 * Returns all workbook embedding targets
 *
 * @param state - The state object
 * @return All embedding targets
 */
export const getEmbeddingTargets = createSelector((state: WorkbookState) => slice(state).embeddingTargetById, Object.values);

/**
 * Returns workbook embedding target with given ID
 *
 * @param state - The state object
 * @param id
 * @return WorkbookEmbeddingTargetDto
 */
export const getEmbeddingTargetById = (state: WorkbookState, id: WorkbookEmbeddingTargetId) => slice(state).embeddingTargetById[id];

/**
 * Return ReportDTO with given ID
 */
export const getReportById = (state: WorkbookState, reportId: ReportId) => slice(state).reportById[reportId];

/**
 * Returns the active worksheets background color if it is set, otherwise the first set background
 * color of any previous worksheet ("auto"-setting). The default is the background color set in theming.
 * If worksheetId is passed, returns the background color of the worksheet with worksheetId.
 *
 * @param state - The state object.
 * @param [worksheetId] - Optional: return background color of worksheet with worksheetId.
 * @return The calculated background color of the worksheet.
 */
export const getWorksheetBackgroundColor = (state: WorkbookState, worksheetId?: WorksheetId): HexColor => {
  const worksheets = getWorksheets(state);
  const worksheet = worksheetId
    ? worksheets.find((ws) => ws.id === worksheetId)
    : getActiveWorksheet(state);
  if (worksheet) {
    const getWsBackgroundColor = (ws: WorksheetDto) => { // Get worksheet background color without "auto"
      return getSetting<HexColor>(ws, WorksheetSettingsKey.toPersistenceKey('WORKSHEET_BACKGROUND_COLOR'));
    };

    let color = getWsBackgroundColor(worksheet);
    if (color != null) {
      return color;
    }

    const worksheetIndex = worksheets.map(ws => ws.id).indexOf(worksheet.id);
    const previousWorksheets = worksheets.slice(0, worksheetIndex);
    while (previousWorksheets.length > 0) {
      color = getWsBackgroundColor(previousWorksheets.pop()!);
      if (color != null) {
        return color;
      }
    }
  }
  return window.Disy.theme['worksheet-bg'] ?? '#FFFFFF';
};

export const getWorksheetViewBorders = (state: WorkbookState, worksheetId?: WorksheetId) => {
  return calculateWorksheetViewBorders(worksheetId ?? getActiveWorksheetId(state), state);
};

export const getAttributes = (state: WorkbookState) => Object.values(slice(state).attributeById);
export const getAttributeById = (state: WorkbookState, attributeId: AttributeId) => {
  return slice(state).activeAttributeById[attributeId as ActiveAttributeId] || slice(state).attributeById[attributeId];
};
export const getAttributesById = (state: WorkbookState, attributeIds: AttributeId[]) => {
  return attributeIds.map(attrId => getAttributeById(state, attrId));
};

export const getAttributesByBundle = (state: WorkbookState, dataViewId: DataViewId) => {
  const dataView = getDataViewById(state, dataViewId);
  if (dataView) {
    return Object.fromEntries(Object.entries(dataView.attributes)
      .map(([ bundle, attributeIds ]) => [
        bundle,
        attributeIds.map(attributeId => getAttributeById(state, attributeId))
      ])) as Record<AttributeBundleName, WorkbookAttributeDto[]> | undefined;
  }
};

export const getAttributeByName = (state: WorkbookState, attributeName: string) => {
  return getAttributes(state).find(attr => attr.name === attributeName);
};

export const getAttributeIdsFromBundle = (state: WorkbookState, dataViewId: DataViewId, bundleName: AttributeBundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  return dataView.attributes[bundleName] ?? [];
};

export const getAttributesFromBundles = (state: WorkbookState, dataViewId: DataViewId, ...bundleNames: AttributeBundleName[]) => {
  const dataView = getDataViewById(state, dataViewId);
  return bundleNames
    .flatMap(bundleName => dataView?.attributes[bundleName] || [])
    .map(attributeId => getAttributeById(state, attributeId));
};

/**
 * This selector checks if an attribute is "reachable" by non-multiplying associations
 * from a given attribute.
 * A target attribute is considered reachable by non-multiplying associations from an origin
 * attribute if the origin's OT and the target's OT are connected via one-to-one or many-to-one
 * associations as seen from the origin's OT.
 * If one of the OTs is a ZOT, then the underlying bOTs of the attribute are considered instead,
 * as "reachability" cannot be accurately computed for zOT.
 * All the reachability is computed on the backend and included in the workbook state.
 * See <code>net.disy.cadenza.workbook.model.impl.CachingReachableObjecttypeCalculator</code>
 * @param state
 * @param originAttribute
 * @param targetAttribute
 */
export const isAttributeToOneReachableFrom = (
  state: WorkbookState,
  originAttribute: WorkbookAttributeDto,
  targetAttribute: WorkbookAttributeDto
) => {
  function getUnderlyingBasicObjecttypeIds (member: WorkbookAttributeDto): ObjecttypeIdString[] {
    // For hierarchies (which have levels), check only the first level for now, as the
    // method is used for maps where only one level is relevant. The highest level can
    // always become selected later and should have the coarsest granularity, so allowing
    // another attribute as "to-one-reachable" by a lower level might lead to errors when
    // the hierarchy is switched to a coarser level.
    return member.levels
      ? member.levels[0].underlyingObjecttypeIds
      : member.underlyingObjecttypeIds;
  }

  const originOtIds = getUnderlyingBasicObjecttypeIds(originAttribute);
  const targetOtIds = getUnderlyingBasicObjecttypeIds(targetAttribute);

  const objecttypeById = slice(state).reachableObjecttypesByBasicObjecttypeId;
  const availableBasicObjecttypes = originOtIds
    .flatMap(otId => {
      assertNonNullable(objecttypeById[otId],
        `In state.reachableObjecttypesByBasicObjecttypeId an Objecttype with id ${otId} is missing.`);
      return objecttypeById[otId].reachableByToOne;
    });
  return targetOtIds.every(otId => availableBasicObjecttypes.includes(otId));
};

export const getReachableObjecttypeIds = (state: WorkbookState, objecttypeId: ObjecttypeIdString): ObjecttypeIdString[] => {
  const objecttype = slice(state).objecttypeById[objecttypeId];
  // unknown objecttypes would crash the ui
  if (objecttype) {
    const objecttypeIds = objecttype.reachableObjecttypes;
    return objecttypeIds ? [ ...objecttypeIds ] : [];
  }
  logger.error(`No objecttype found by id "${objecttypeId}"`);
  return [];
};

/**
 * Return all attributes which are reachable by the main OT of the specified workbook's views.
 */
export const getReachableAttributesByWorksheetId = (state: WorkbookState, worksheetId: WorksheetId): AttributeId[] => {
  return uniq( // Since every worksheet could list the same attributes (e.g. when copy) we deduplicate
    slice(state).worksheetById[worksheetId].views
      .flatMap(viewId => getReachableAttributesByViewId(state, viewId)));
};

/**
 * Return all attributes which are reachable by the main OT of the specified view.
 */
export const getReachableAttributesByViewId = (state: WorkbookState, viewId: ViewId): AttributeId[] => {
  const currentSlice = slice(state);

  const viewById = currentSlice.viewById;
  const dataViewById = currentSlice.dataViewById;
  const objecttypeById = currentSlice.objecttypeById;

  return uniq(  // Since every dataView could list the same attributes (e.g. when copy) we deduplicate
    viewById[viewId].dataViews
      .flatMap(dataViewId => dataViewById[dataViewId])
      .flatMap(dataView => {
        // When removing the Objecttype from the workbook, the reference to it is kept in the data
        // view (which then fails validation and the user is informed). Such a dangling reference
        // would create a NPE here. Returning [] in such cases allows to compile the desired list.
        return objecttypeById[dataView.mainObjecttype.id]?.reachableObjecttypes || [];
      })
      .flatMap(objecttypeId => objecttypeById[objecttypeId].attributes));
};

// This selector returns all object types, including ones with restricted visibility.
// In most cases only visible unrestricted object types are required, so use getVisibleObjectTypes or getVisibleObjecttypeIds instead.
export const getObjecttypes: (state: WorkbookState) => Objecttype[] = createSelector((state: WorkbookState) => slice(state).objecttypeById, Object.values);

export const getUnavailableObjecttypeIds = (state: WorkbookState) => slice(state).unavailableObjecttypeIds;

const getUsedAttributes = createSelector(
  [ (state) => slice(state).dataViewById,
    (state) => slice(state).conditionById,
    (state) => slice(state).drillThroughTargets
  ], (dataViewById, conditionById, drillThroughTargets) => new Set(
    [
      ...Object.values(dataViewById).flatMap(dv => Object.values(dv.attributes)
        .flat()
        .map(getBaseAttributeId)),
      ...Object.values(conditionById).flatMap(c => [
        ...c.attributeIds,
        ...(c.conditionConfiguration && 'additionalAttributes' in c.conditionConfiguration ? c.conditionConfiguration.additionalAttributes : [])
      ]),
      ...Object.values(drillThroughTargets).map(dt => dt.attributeId)
    ]));

export const isObjecttypeUsed = (state: WorkbookState, objecttypeId: ObjecttypeIdString) =>
  [ ...getUsedAttributes(state) ].some(id => isPartOfObjecttype(id, objecttypeId))
  || Object.values(slice(state).dataViewById).some(dataView => dataView.mainObjecttype.id === objecttypeId);

export const isAttributeUsed = (state: WorkbookState, attributeId: AttributeId) =>
  getUsedAttributes(state).has(attributeId);

export const getDataProtection = (state: WorkbookState) => {
  return slice(state).dataProtection;
};
export const getObjecttypeById = (state: WorkbookState, objecttypeId: ObjecttypeIdString) => {
  return slice(state).objecttypeById[objecttypeId];
};
export const getAllAttributesFromObjectType = createSelector(
  [ getObjecttypeById, (state) => slice(state).attributeById, (state) => slice(state).objecttypeById ],
  (objecttype, attributesById, objecttypeById) => {
    const attributes = objecttype.attributes
      .map(attributeId => attributesById[attributeId]);
    const attributesFromEnrichments = objecttype.enrichments
      ?.flatMap(enrichment => {
        const enrichmentObjecttype = objecttypeById[enrichment.objecttypeId];
        return enrichmentObjecttype.attributes.map(attributeId => attributesById[attributeId]);
      }) ?? [];
    attributes.push(...attributesFromEnrichments);
    return attributes;
  }
);

export const getAllEnrichmentsFromObjectType = createSelector(
  [ getObjecttypeById ],
  (objecttype) => objecttype?.enrichments ?? []
);

export const getAllEnrichmentObjecttypeIds = createSelector(
  [ (state) => slice(state).objecttypeById ],
  objecttypes => Object.values(objecttypes)
    .flatMap(ot => ot.enrichments ?? [])
    .map(enrichment => enrichment.objecttypeId)
);

export const getVisibleObjecttypes = createSelector(getObjecttypes, objecttypes => objecttypes
  .filter(objecttype => objecttype.visible && !isVisibilityRestricted(objecttype.privileges))
  .sort((a, b) => compareWithLocale(a.printName, b.printName)));
export const getVisibleObjecttypeIds = createSelector(getVisibleObjecttypes, visibleObjecttypes => visibleObjecttypes.map(({ id }) => id));
export const getObjecttypesWithAttributes = createSelector(getObjecttypes, resolveObjecttypeAttributes);
export const getVisibleObjecttypesWithoutTheirEnrichments = createSelector(getVisibleObjecttypes, objecttypes => {
  const visibleEnrichmentsOnVisibleObjecttypeObjecttypeIds = [ ...new Set(objecttypes
    .flatMap(objecttype => objecttype.enrichments ?? [])
    .map(enrichment => enrichment.objecttypeId)) ];
  return objecttypes.filter(objecttype => !visibleEnrichmentsOnVisibleObjecttypeObjecttypeIds.includes(objecttype.id))
    .sort((a, b) => compareWithLocale(a.printName, b.printName));
});

function isVisibilityRestricted (privileges: ObjectPrivilege[]) {
  return privileges?.length > 0 && !hasPrivilege(privileges, 'visible');
}

const getObjecttypesByAttributeId = createSelector(getObjecttypes,
  (objecttypes) => Object.fromEntries(objecttypes
    .flatMap((objecttype) => objecttype.attributes
      .map((attributeId) => [ attributeId, objecttype ]))));

export const getObjecttypeByAttributeId = (state: WorkbookState, attributeId: AttributeId) => {
  return getObjecttypesByAttributeId(state)[attributeId];
};

const getEnrichmentsByAttributeId = createSelector(
  (state: WorkbookState) => slice(state).objecttypeById,
  (objecttypeById) => {
    return Object.fromEntries(Object.values(objecttypeById)
      .flatMap(objecttype => objecttype.enrichments)
      .flatMap(enrichment => enrichment
        ? objecttypeById[enrichment.objecttypeId].attributes.map(attributeId => [ attributeId, enrichment ])
        : []));
  }
);

export const getEnrichmentByAttributeId = (state: WorkbookState, attributeId: AttributeId) => {
  return getEnrichmentsByAttributeId(state)[attributeId];
};

export const hasObjecttypeWithObjectId = (state: WorkbookState, objecttypeIdString: ObjecttypeIdString) => {
  return !!getObjecttypeById(state, objecttypeIdString);
};

export const hasObjecttypeWithPrintName = (state: WorkbookState, name: string) => {
  return getObjecttypes(state).some(ot => ot.printName === name);
};

export const hasBoundObjecttypes = (state: WorkbookState) => {
  return getObjecttypes(state).some(ot => isObjecttypeBound(ot));
};

export const getDataSources = (state: WorkbookState) => slice(state).datasources;

export const getVisibleDataSources = createSelector(getDataSources, getVisibleObjecttypes, filterEmptyDatasources);

function filterEmptyDatasources (datasources: WorkbookDataSourceDto[], visibleObjecttypes: Objecttype[]) {
  return datasources.filter(({ name }) => visibleObjecttypes.some(({ dataSourceId }) => dataSourceId === name));
}

export function getDataSourceByObjecttypeId (state: WorkbookState, objecttypeId: ObjecttypeIdString) {
  const objecttype = getObjecttypeById(state, objecttypeId);
  return getDataSources(state).find(datasource => datasource.name === objecttype.dataSourceId
    && datasource.repositoryName === objecttype.dataSourceRepositoryName)!;
}

export function hasObjecttypeDatasourceCapabilities (state: WorkbookState, objecttypeId: ObjecttypeIdString, capabilities: DataSourceCapability[]) {
  const dataSource = getDataSourceByObjecttypeId(state, objecttypeId);
  return capabilities.every(capability => hasCapability(dataSource, capability));
}

export function isObjecttypeInCmdb (state: WorkbookState, objecttypeId: ObjecttypeIdString) {
  return getDataSourceByObjecttypeId(state, objecttypeId).cmdb;
}

export const getConditionsAndGroups = (state: WorkbookState) => slice(state).conditionsAndGroups;
export const getConditionById = (state: WorkbookState, conditionId: ConditionId) => slice(state).conditionById[conditionId];

export const getConditionsByGroupId = (state: WorkbookState, conditionGroupId: ConditionGroupId) =>
  getConditionGroupConditionIds(state, conditionGroupId).map(conditionId => getConditionById(state, conditionId));

export const getConditionDeactivated = (state: WorkbookState, conditionId: ConditionId) =>
  getDeactivated(getConditionById(state, conditionId));

export const isConditionDeactivatedForActiveWorksheet = (state: WorkbookState, conditionId: ConditionId) =>
  isDeactivatedForWorksheet(getConditionById(state, conditionId), getActiveWorksheetId(state));

export const isConditionDeactivatedForAnyWorksheet = (state: WorkbookState, conditionId: ConditionId) =>
  isDeactivatedForWorksheet(getConditionById(state, conditionId), getActiveWorksheetId(state));

export const isConditionHidden = (state: WorkbookState, conditionId: ConditionId) =>
  isHidden(getConditionById(state, conditionId), getActiveWorksheetId(state));

export const isConditionGroupHidden = (state: WorkbookState, conditionGroupId: ConditionGroupId) =>
  areAllHidden(getConditionsByGroupId(state, conditionGroupId), getActiveWorksheetId(state));

export const isConditionMandatory = (state: WorkbookState, conditionId: ConditionId) =>
  isMandatory(getConditionById(state, conditionId));

export function getAffectedViewsByHierarchy (state: WorkbookState) {
  return slice(state).affectedViewsByHierarchyId;
}

function getConditionsInAnalysisContext (state: WorkbookState) {
  const result: ConditionId[] = [];
  for (const id of slice(state).conditionsAndGroups) {
    result.push(...(slice(state).conditionById[id as ConditionId]
      ? [ id as ConditionId ]
      : slice(state).conditionGroupById[id as ConditionGroupId].conditions));
  }
  return result;
}

export function isAnalysisContextAffectingViews (state: WorkbookState) {
  return getConditionsInAnalysisContext(state).some(conditionId => {
    const affectedViewIds = getViewIdsAffectedByCondition(store.getState(), conditionId);
    if (affectedViewIds.length > 0) {
      const condition = slice(state).conditionById[conditionId];
      return hasValuesSet(condition) && isConditionDeactivatedForActiveWorksheet(state, conditionId) !== true;
    }
  });
}

export function getViewIdsAffectedByCondition (state: WorkbookState, conditionId: ConditionId, withLocalConditions = false) {
  return slice(state).affectedViewsByConditionId[conditionId]
    ?.[withLocalConditions ? 'affectedViewsWithLocalConditions' : 'affectedViews']
    ?.[getActiveWorksheetId(state)]
    ?? [];
}

function getDataViewIdsAffectedByCondition (state: WorkbookState, conditionId: ConditionId) {
  return slice(state).affectedViewsByConditionId[conditionId]
    ?.affectedDataViews
    ?.[getActiveWorksheetId(state)]
    ?? [];
}

export function getAttributesByDataViewIds (state: WorkbookState, conditionId: ConditionId) {
  return slice(state).affectedViewsByConditionId[conditionId]
    ?.affectedAttributesByDataViewId
    ?? {};
}

export const getConditionsById = (state: WorkbookState) => slice(state).conditionById;
export const getConditionGroupById = (state: WorkbookState, conditionId: ConditionGroupId) => slice(state).conditionGroupById[conditionId];
export const getConditionGroups = (state: WorkbookState) => Object.values(slice(state).conditionGroupById);

export const getConditionGroupConditionIds = (state: WorkbookState, conditionId: ConditionGroupId) => getConditionGroupById(state, conditionId).conditions;

export const getHierarchies = (state: WorkbookState) => Object.values(slice(state).hierarchyById);

export const isGlobalCondition = (state: WorkbookState, conditionId: ConditionId) => [
  ...slice(state).conditionsAndGroups,
  ...getConditionGroups(state).flatMap(group => group.conditions)
].includes(conditionId);

export const getGlobalConditionIds = (state: WorkbookState) => {
  return Object.keys(getConditionsById(state))
    .map(key => key as ConditionId)
    .filter(conditionId => isGlobalCondition(state, conditionId));
};

export const getGlobalConditionsHavingValue = (state: WorkbookState) => {
  return Object.values(slice(state).conditionById)
    .filter(condition => isGlobalCondition(state, condition.id) && conditionHasValues(condition));
};

export const hasSpatialConditions = createSelector(
  getConditionsById,
  conditionById => {
    const conditions = Object.values(conditionById);
    return conditions.some(({ type }) => type === 'PolygonCondition');
  });

export const isFilteredByCondition = (state: WorkbookState, dataViewId: DataViewId, conditionId: ConditionId) => {
  return getDataViewIdsAffectedByCondition(state, conditionId).includes(dataViewId);
};

export const hasDataViewConditions = (state: WorkbookState, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId).conditions.length > 0;
};

export const isDataViewAffectedByWorkbookConditions = (state: WorkbookState, dataViewId: DataViewId) => {
  const globalAndLocalConditionsById = getConditionsById(state);
  const globalAndLocalConditionIds = Object.keys(globalAndLocalConditionsById) as ConditionId[];
  const globalConditionIds = globalAndLocalConditionIds
    .filter(conditionId => isGlobalCondition(state, conditionId));

  // we check if there's at least one workbook condition that affects our dataView
  return globalConditionIds.some(conditionId => isFilteredByCondition(state, dataViewId, conditionId));
};

export const getViews = createSelector((state: WorkbookState) => slice(state).viewById, items => Object.values(items) as ViewDto[]);
export const getViewIds = createSelector((state: WorkbookState) => slice(state).viewById, (viewById) => Object.keys(viewById) as ViewId[]);
export const getActiveWorksheetViewIds = (state: WorkbookState) => getActiveWorksheet(state)?.views ?? [];
export const getActiveViewsOfWorksheet = (state: WorkbookState) => getActiveWorksheetViewIds(state).map(viewId => getViewById(slice(state), viewId));
export const getActiveView = (state: WorkbookState) => {
  const activeViewId = getActiveViewId(state);
  if (activeViewId) {
    return getViewById(state, activeViewId);
  }
};

export const getActiveOrFirstMapViewId = createSelector(
  [ getActiveView, getViews ],
  (activeView, views) => {
    if (activeView?.type === ViewType.MAP) {
      return activeView.id;
    }
    return views.find(view => view.type === ViewType.MAP)?.id;
  }
);

export const getViewById = (
  state: WorkbookState,
  viewId: ViewId) => addViewMetadata(state, slice(state).viewById[viewId])!;
export const getViewByDataViewId = (
  state: WorkbookState,
  dataViewId: DataViewId) => addViewMetadata(state, getActiveViewsOfWorksheet(state).find(view => view.dataViews.includes(dataViewId)))!;

export const getViewByLayerId = (
  state: WorkbookState,
  layerId: LayerId) => addViewMetadata(state, getActiveViewsOfWorksheet(state).find(view => Object.keys(view.layerDefinitions ?? {}).includes(layerId)))!;

export const getLayerGroupsFromView = (state: WorkbookState, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  return Object.values(view.layerDefinitions || []).filter(layer => layer.structureType !== 'SINGLE');
};

export const getActiveLayerId = (state: WorkbookState) => {
  return slice(state).activeLayerId;
};

export const getVisualisationKeyByViewId = (state: WorkbookState, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  if (!view) {
    return;
  }
  const dataViewId = view.dataViews[0];
  if (dataViewId) {
    return getDataViewById(state, dataViewId).visualizationKey;
  }
};

export const getViewNamesByViewId = (state: WorkbookState, viewId: ViewId) => {
  const view = slice(state).viewById[viewId];
  return {
    name: view?.name,
    defaultName: view?.defaultName
  };
};

function addViewMetadata (state: WorkbookState, view?: ViewDto): ViewWithMetadata | undefined {
  if (view) {
    return { ...view, metadata: slice(state).viewMetadataById[view.id] ?? {} };
  }
}

export const getViewMetadata = <T extends ViewMetadata = ViewMetadata> (state: WorkbookState, viewId: ViewId) => slice(state).viewMetadataById[viewId] as T | undefined;

export const isViewLoading = (state: WorkbookState, viewId: ViewId) => {
  const loadingState = getViewMetadata(state, viewId)?.loadingState as LoadingState;
  return [ 'LOADING', 'LOADING_LONG' ].includes(loadingState);
};

export const isViewRendering = (state: WorkbookState, viewId: ViewId) => {
  const metadata = getViewMetadata(state, viewId);
  if (metadata && 'isRendering' in metadata) {
    return metadata.isRendering;
  }
  return false;
};

export const isAnyViewLoading = (state: WorkbookState) => getViewIds(state)
  .some(viewId => isViewLoading(state, viewId));

export const isAnyViewLoadingOrRendering = (state: WorkbookState) => getViewIds(state)
  .some(viewId => isViewLoading(state, viewId) || isViewRendering(state, viewId));

export const getDataViewById = (state: WorkbookState, dataViewId: DataViewId) => slice(state).dataViewById[dataViewId];

export const getDataViewStatus = (state: WorkbookState, dataViewId: DataViewId) => slice(state).dataViewById[dataViewId].status;

export function hasWorkbookInvalidStatus () {
  const hasInvalidDataViews = Object.values(slice(store.getState()).dataViewById).some(dataView => dataView.status !== 'VALID');
  const hasInvalidLayers = getViews(store.getState())
    .map(viewDto => viewDto.layerDefinitions ? Object.values(viewDto.layerDefinitions) : [])
    .flatMap(layerDtos => layerDtos.flatMap(layerDto => layerDto.status))
    .some(status => status !== 'OK');
  const unavailableObjecttypeIds = getUnavailableObjecttypeIds(store.getState());
  const hasInvalidAttributes = getAttributes(store.getState()).some(attribute => attribute.status !== 'OK');
  const hasInvalidConditions = getOrderedConditions(store.getState()).map(condition => condition.status)
    .some(status => status !== 'VALID');
  return hasInvalidDataViews || hasInvalidLayers || unavailableObjecttypeIds.length > 0 || hasInvalidAttributes || hasInvalidConditions;
}

const getDataViewIdsUsingObjecttypes = (state: WorkbookState, objecttypeIds: ObjecttypeIdString[]) => {
  const dataViewIds = [];

  for (const [ dataViewId, dataView ] of entries(slice(state).dataViewById)) {
    if (Object.values(dataView.attributes) // Only bundle attributes
      .flat() // Unwrap bundles attributes
      .some(attributeId => objecttypeIds.includes(getAttributeById(state, attributeId).objecttypeId))) {
      dataViewIds.push(dataViewId);
    }
  }

  return dataViewIds;
};

function getDataViewIdsUsingObjecttype (state: WorkbookState, objecttypeId: ObjecttypeIdString) {
  return getDataViewIdsUsingObjecttypes(state, [ objecttypeId ]);
}

export const getDataViewIdsByViewId = (state: WorkbookState, viewId: ViewId) => getViewById(state, viewId).dataViews;

export const getDataViewsByViewId = (state: WorkbookState, viewId: ViewId) =>
  getViewById(state, viewId).dataViews
    .map(dataViewId => getDataViewById(state, dataViewId));

export function getViewIdsByWorksheetId (state: WorkbookState, worksheetId: WorksheetId) {
  return getWorksheetById(state, worksheetId).views;
}

export function getDataViewsByWorksheetId (state: WorkbookState, worksheetId: WorksheetId) {
  return getViewIdsByWorksheetId(state, worksheetId).flatMap(viewId => getDataViewsByViewId(state, viewId));
}

export function getMainObjecttypeIdsForActiveWorksheet (state: WorkbookState) {
  const worksheetId = getActiveWorksheetId(state);
  const dataViews = getDataViewsByWorksheetId(state, worksheetId);
  const objecttypeIds = dataViews.map(dataView => dataView.mainObjecttype.id)
    .filter(objecttypeId => UNSET_OBJECTTYPE_ID !== objecttypeId);
  return [ ...new Set(objecttypeIds) ];
}

const getNotificationsForViewByNotificationId = (state: WorkbookState, viewId: ViewId) => {
  return slice(state).notificationsByView[viewId] ?? {};
};

export const getNotificationsForView = (state: WorkbookState, viewId: ViewId) => {
  return Object.values(
    getNotificationsForViewByNotificationId(state, viewId)
  );
};

export const hasViewFatalError = (state: WorkbookState, viewId: ViewId) => {
  return getNotificationsForView(state, viewId)
    .some(notification => notification.level === 'FATAL');
};

export const getNotificationsForViewByTypes = (state: WorkbookState, viewId: ViewId, types: NotificationType[]) => {
  return getNotificationsForView(state, viewId)
    .filter(notification => types.includes(notification.type));
};

export const getDataViewNotificationsForView = (state: WorkbookState, viewId: ViewId) => {
  return getNotificationsForView(state, viewId).filter(isDataViewNotification);
};

export const getDataViewNotificationForActiveView = (state: WorkbookState) => {
  const activeViewId = getActiveViewId(state);
  return activeViewId ? getDataViewNotificationsForView(state, activeViewId) : [];
};

export const getLayerNotificationsForView = (state: WorkbookState, viewId: ViewId) => {
  return getNotificationsForView(state, viewId).filter(isLayerNotification);
};

export const getNotificationsForDataView = (state: WorkbookState, viewId: ViewId, dataViewId: DataViewId) => {
  return getDataViewNotificationsForView(state, viewId)
    .filter(notification => notification.dataViewId === dataViewId);
};

export const getNotificationsForLayer = (state: WorkbookState, viewId: ViewId, layerId: LayerId) => {
  return getLayerNotificationsForView(state, viewId)
    .filter(notification => notification.layerId === layerId);
};

export const dataViewHasMissingAttributes = (state: WorkbookState, viewId: ViewId, dataViewId: DataViewId) => {
  return getNotificationsForDataView(state, viewId, dataViewId)
    .some(notification => notification.type === 'MISSING_ATTRIBUTE');
};

export const getDataViewNamesByDataViewId = (state: WorkbookState, dataViewId: DataViewId) => {
  const dataView = slice(state).dataViewById[dataViewId];
  return {
    name: dataView.name,
    defaultName: dataView.defaultName
  };
};

export const getViewsAffectedByCondition = (state: WorkbookState, conditionId: ConditionId) => {
  return getViewIdsAffectedByCondition(state, conditionId).map(viewId => getViewById(state, viewId));
};

export const getViewsThatOverrideConditionLocally = (state: WorkbookState, conditionId: ConditionId) => {
  const condition = getConditionById(state, conditionId);
  assert(condition, `No condition with ID=${conditionId} present in the state.`);

  // Spatial conditions actually can't be overridden locally, however they are still present at the
  // view level because they can apply to a different attribute for each view.
  if (condition.type === 'PolygonCondition') {
    return [];
  }

  return getViewIdsAffectedByCondition(state, conditionId, true).map(viewId => getViewById(state, viewId));
};

export const getObjecttypesByDataViewId = (state: WorkbookState, dataViewId: DataViewId) => {
  const uniqueObjecttypes = new Set<Objecttype>();
  const attributeIds: AttributeId[] = Object.values(getDataViewById(state, dataViewId).attributes).flat();
  for (const attributeId of attributeIds) {
    const objecttype = getObjecttypeById(state, getAttributeById(state, attributeId).objecttypeId);
    if (objecttype) {
      uniqueObjecttypes.add(objecttype);
    }
  }
  return Array.from(uniqueObjecttypes);
};

export const getObjecttypesByViewId = createSelector(
  (state: WorkbookState) => state,
  getDataViewIdsByViewId,
  (
    state,
    dataViewIds
  ): Objecttype[] => {
    return Array.from(dataViewIds
      .reduce((uniqueObjecttypes, dataViewId) => {
        getObjecttypesByDataViewId(state, dataViewId).forEach(ot => uniqueObjecttypes.add(ot));
        return uniqueObjecttypes;
      }, new Set<Objecttype>()));
  }
);

export const getConditionsByViewId = createSelector(
  getActiveWorksheetId,
  getConditionsById,
  (state: WorkbookState) => slice(state).viewById,
  (state: WorkbookState) => slice(state).affectedViewsByConditionId,
  (
    activeWorksheetId,
    conditionsById,
    viewById,
    affectedViewsByConditionId
  ) => {
    const conditionsByViewId: Record<ViewId, ConditionDto[]> = {};

    Object.values(conditionsById).forEach(condition => {
      const viewsAffectedByCondition = affectedViewsByConditionId[condition.id]
        ?.affectedViews
        ?.[activeWorksheetId]
        ?.map(viewId => viewById[viewId]) || [];

      viewsAffectedByCondition.forEach(view => {
        if (!conditionsByViewId[view.id]) {
          conditionsByViewId[view.id] = [];
        }
        if (!conditionsByViewId[view.id].includes(condition)) {
          conditionsByViewId[view.id].push(condition);
        }
      });
    });

    return conditionsByViewId;
  }
);

export const hasConditionsWithMissingMandatoryValue = createSelector(
  getConditionsById,
  conditionsById => {
    for (const condition of Object.values(conditionsById)) {
      if (condition.settings.mandatory && !conditionHasValues(condition)) {
        return true;
      }
    }
    return false;
  });

export const getSpatialConditionsForAllViews = createSelector(
  getConditionsByViewId,
  (conditionsByViewId) => {
    const spatialConditionsByViewId: Record<ViewId, ConditionDto[]> = {};
    entries(conditionsByViewId).forEach(([ viewId, conditions ]) => {
      const spatialConditions = conditions.filter(condition => condition.type === 'PolygonCondition');
      if (spatialConditions.length) {
        spatialConditionsByViewId[viewId] = spatialConditions;
      }
    });
    return spatialConditionsByViewId;
  }
);

export const getEffectiveSpatialConditionsForAllViews = createSelector(
  getSpatialConditionsForAllViews,
  (spatialConditionsByViewId) => {
    const effectiveSpatialConditionsByViewId: Record<ViewId, ConditionDto[]> = {};
    entries(spatialConditionsByViewId).forEach(([ viewId, conditions ]) => {
      const effectiveSpatialConditions = conditions.filter(isSpatialConditionEffective);
      if (effectiveSpatialConditions.length) {
        effectiveSpatialConditionsByViewId[viewId] = effectiveSpatialConditions;
      }
    });

    return effectiveSpatialConditionsByViewId;
  }
);

function isSpatialConditionEffective (spatialCondition: ConditionDto) {
  // it should have a value with at least one polygon
  return conditionHasValues(spatialCondition);
}

export const getSpatialConditionsForView = (state: WorkbookState, viewId: ViewId) => getSpatialConditionsForAllViews(state)[viewId] ?? [];

export const getEffectiveSpatialConditionsForView = (state: WorkbookState, viewId: ViewId) => getEffectiveSpatialConditionsForAllViews(state)[viewId] ?? [];

export const getOrderedConditions = (state: WorkbookState) => getConditionsAndGroups(state)
  .flatMap(conditionOrGroupId => {
    const conditionGroup = getConditionGroupById(state, conditionOrGroupId as ConditionGroupId);
    return conditionGroup ? conditionGroup.conditions : conditionOrGroupId as ConditionId;
  })
  .map(conditionId => getConditionById(state, conditionId));

export const getAllSpatialConditions = (state: WorkbookState) => getOrderedConditions(state)
  .filter(condition => condition.type === 'PolygonCondition');

export const viewHasEffectiveSpatialFilter = (state: WorkbookState, viewId: ViewId) => getSpatialConditionsForView(state, viewId).length > 0;

export const isSpatialConditionGeometryVisibleInView = (state: WorkbookState, viewId: ViewId) => slice(state).viewById[viewId].settings[MapViewSettingsKey.toPersistenceKey('DISPLAY_SPATIAL_FILTER_GEOMETRIES')];

export const getActiveViewId = (state: WorkbookState) => slice(state).activeViewId;
export const isActiveView = (state: WorkbookState, viewId: ViewId) => (getActiveViewId(state) === viewId);

export const isViewInvalid = (state: WorkbookState, viewId: ViewId) => {
  const invalidViewsOrDataViews = getInvalidViewsOrDataViews(state);
  return getInvalidViewsOrDataViews(state).includes(viewId)
    || getViewById(state, viewId).dataViews.some(dataViewId => invalidViewsOrDataViews.includes(dataViewId));
};

export const isViewAffectedBySelection = (state: WorkbookState, viewId: ViewId) => {
  const dataViewsAffectedBySelectionChange = getDataViewsAffectedBySelectionChange(state);
  return getViewById(state, viewId).dataViews
    .some(dataViewId => dataViewsAffectedBySelectionChange.includes(dataViewId));
};

export const isViewInvalidOrAffectedBySelection = (state: WorkbookState, viewId: ViewId) => {
  const invalidViewsOrDataViews = getInvalidViewsOrDataViews(state);
  if (invalidViewsOrDataViews.includes(viewId)) {
    return true;
  }
  const dataViewsAffectedBySelectionChange = getDataViewsAffectedBySelectionChange(state);
  return getViewById(state, viewId).dataViews.some(dataViewId => invalidViewsOrDataViews.includes(dataViewId)
    || dataViewsAffectedBySelectionChange.includes(dataViewId));
};

export const getInvalidViewsOrDataViews = (state: WorkbookState) => slice(state).invalidViewsOrDataViews;
export const getInvalidViewsOrDataViewsOfActiveWorksheet = (state: WorkbookState) => {
  const viewIds = getActiveWorksheetViewIds(state);
  const dataViewIds = viewIds.flatMap(viewId => getDataViewIdsByViewId(state, viewId));
  return slice(state).invalidViewsOrDataViews
    .filter(viewIdOrDataViewId => viewIds.includes(viewIdOrDataViewId as ViewId) || dataViewIds.includes(viewIdOrDataViewId as DataViewId));
};
export const isActiveWorksheetDirty = (state: WorkbookState) => getInvalidViewsOrDataViewsOfActiveWorksheet(state).length > 0;
export const getDataViewsAffectedBySelectionChange = (state: WorkbookState) => slice(state).dataViewsAffectedBySelectionChange;

export const getMaximizedViewId = (state: WorkbookState) => slice(state).maximizedViewId;
export const isMaximizedView = (viewId: ViewId, state: WorkbookState = store.getState()) => getMaximizedViewId(state) === viewId;
export const hasForceMaximizedView = (state: WorkbookState) => slice(state).forceMaximizePreviousState != null;
export const getSelection = (state: WorkbookState) => slice(state).selection;

export const getDataViewIdsWithAllowedSelection = (state: WorkbookState) => (Object.keys(slice(state).dataViewById) as DataViewId[])
  .filter(dataViewId => getSettingOrSettingDefault(
    getDataViewById(state, dataViewId),
    CommonDataViewSettingsKey.toPersistenceKey('SELECTION_ENABLED')));

export const isViewOnDemand = (state: WorkbookState, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  const isAnyDataViewOnDemand = view.dataViews.some(dataViewId => {
    const dataView = getDataViewById(state, dataViewId);
    return isReportMode(state) || isDataViewOnDemand(dataView);
  });
  return isAnyDataViewOnDemand && shouldOnDemandBeConsidered(state, slice(state).lastCommand);
};

export const isFeatureInfoEnabled = (state: WorkbookState, dataViewId: DataViewId, bundleName: AttributeBundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_ENABLED'), 'true') === 'true';
};

// returns setting(s) (without default value) for a given key(s)
export const getDataViewSettings = (state: WorkbookState, dataViewId: DataViewId, keys: string | string[]): Record<string, unknown> => {
  const settings = getDataViewById(state, dataViewId).settings;
  const result: Record<string, unknown> = {};
  (Array.isArray(keys) ? keys : [ keys ])
    .forEach(key => {
      result[key] = getSetting(settings, key);
    });
  return result;
};

// returns setting value (without default value) for a given key
export function getDataViewSetting<T> (state: WorkbookState, dataViewId: DataViewId, key: string): T {
  return getDataViewSettings(state, dataViewId, key)[key] as T;
}

// returns setting value or default setting value for a given key
export function getDataViewSettingOrSettingDefault<T> (state: WorkbookState, dataViewId: DataViewId, key: string): T {
  return getSettingOrSettingDefault(getDataViewById(state, dataViewId).settings, key) as T;
}

// returns default setting value for a given key
export function getDataViewDefaultSetting<T> (state: WorkbookState, dataViewId: DataViewId, key: string): T {
  return getDefaultSetting(getDataViewById(state, dataViewId).settings, key) as T;
}

export const isFeatureInfoAutoModeEnabled = (state: WorkbookState, dataViewId: DataViewId, bundleName: AttributeBundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_AUTO_MODE_ENABLED'), 'true') === 'true';
};

export const getObjectInfoHeadlineType = (state: WorkbookState, dataViewId: DataViewId, bundleName: AttributeBundleName): FeatureInfoHeadlineType => {
  if (isFeatureInfoAutoModeEnabled(state, dataViewId, bundleName)) {
    return 'auto';
  }
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_HEADLINE_TYPE'), 'auto');
};

export const getObjectInfoCustomHeadline = (state: WorkbookState, dataViewId: DataViewId, bundleName: AttributeBundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_HEADLINE_CUSTOM'), undefined);
};

export const getIsPieChartSliceValueResolvingEnabled = (state: WorkbookState, dataViewId: DataViewId, bundleName: AttributeBundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  const settingsContainer = { settings: dataView.attributeBundleSettings[bundleName] ?? {} };
  return getSetting(settingsContainer, AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_PIE_CHART_SLICE_RESOLVING_ENABLED'), 'true') === 'true';
};

export const getObjectInfoAttributeBundleName = (state: WorkbookState, dataViewId: DataViewId, objecttypeId: ObjecttypeIdString) => {
  const dataView = getDataViewById(state, dataViewId);
  const objecttypeIdToBundleName = dataView.objecttypeIdToAttributeBundleName ?? {};
  return objecttypeIdToBundleName[objecttypeId];
};

export const selectAttributeBundleSettings = (state: WorkbookState, dataViewId: DataViewId) => {
  const dataView = getDataViewById(state, dataViewId);
  return dataView?.attributeBundleSettings;
};

export const dataViewPointSymbolization = (state: WorkbookState, dataViewId: DataViewId) => {
  const dataView = getDataViewById(state, dataViewId);
  return getSetting(dataView?.settings, MapDataViewSettingsKey.toPersistenceKey('POINT_SYMBOLIZATION'));
};

export const getAttributeBundleSettings = (state: WorkbookState, dataViewId: DataViewId, bundleName: AttributeBundleName) => {
  const dataView = getDataViewById(state, dataViewId);
  return dataView?.attributeBundleSettings[bundleName];
};

/**
 * Clone view to a different worksheet
 *
 * Note: This is just a helper function. Since it's not a reducer it mustn't modify the state. Like
 * regular code, it calls selectors and dispatches a thunk.
 */
export const copyViewToWorksheet = (store: Store, viewId: ViewId, targetWorksheetId: WorksheetId) => {
  const copyViewPayload: CopyViewCommandPayload = {
    viewId,
    targetWorksheetId
  };
  const view = getViewById(store.getState(), viewId);
  if (view.type === ViewType.MAP) {
    const currentMapExtent = loadExtentFromSession(getWorkbookIdAndHash(store.getState()).id, viewId);
    if (currentMapExtent) {
      const changeCurrentExtentPayload: ChangeCurrentExtentCommandPayload = {
        viewId,
        currentExtent: currentMapExtent
      };

      const copyViewCommand = Command.COPY_VIEW(copyViewPayload);
      const changeCurrentExtentCommand = Command.CHANGE_CURRENT_EXTENT(changeCurrentExtentPayload);
      const composite = Command.COMPOSITE_COMMAND({
        commands: [ changeCurrentExtentCommand, copyViewCommand ]
      });
      return store.dispatch(executeCommand(composite));
    }
  }
  return store.dispatch(executeCommand(Command.COPY_VIEW(copyViewPayload)));
};

/**
 * Create a new worksheet
 */
export const addWorksheet = (store: Store, worksheetName: string) => {
  return store.dispatch(executeCommand(Command.ADD_WORKSHEET({ worksheetName }))).then(() => {
    const worksheets = getWorksheets(store.getState());
    // last one is the new worksheet
    return worksheets[worksheets.length - 1].id;
  });
};

/**
 * Creates duplicate of given worksheet
 *
 * @param store - Redux store
 * @param worksheetName - name of the new worksheet to be created
 * @param sourceWorksheetId - id of the
 *   source worksheet
 * @return executeCommand thunk
 */
export const duplicateWorksheet = (store: Store, worksheetName: string, sourceWorksheetId: WorksheetId) => {
  const copyWorksheetCommand = Command.COPY_WORKSHEET({ worksheetName, sourceWorksheetId });
  return store.dispatch(executeCommand(copyWorksheetCommand))
    .then(() => {
      const worksheets = getWorksheets(store.getState());
      // duplicated worksheet is always placed after the original
      const sourceWorksheetIndex = worksheets.findIndex(({ id }) => id === sourceWorksheetId);
      return worksheets[sourceWorksheetIndex + 1].id;
    });
};

export const removeWorksheet = (store: Store, worksheetId: WorksheetId) => {
  return store.dispatch(executeCommand(Command.REMOVE_WORKSHEET({ worksheetId })));
};

export const setWorksheetSettings = (store: Store, worksheetId: WorksheetId, settings: Settings) => {
  return store.dispatch(executeCommand(Command.CHANGE_WORKSHEET_SETTINGS({ worksheetId, settings })));
};

export const setWorkbookSettings = (store: Store, settings: Settings) => {
  return store.dispatch(executeCommand(Command.CHANGE_WORKBOOK_SETTINGS({ settings })));
};

/**
 * Save current extent for all map views in the workbook.
 *
 * @param store - Redux store
 * @return Promise of the new workbook state or void, if there was no map views
 */
export const saveMapViewsExtent = (store: Store) => {
  const workbookIdAndHash = getWorkbookIdAndHash(store.getState());
  const mapViews = getViews(store.getState()).filter((view) => view.type === ViewType.MAP);
  const commands = mapViews.flatMap(({ id: viewId }) => {
    const mapExtent = loadExtentFromSession(workbookIdAndHash.id, viewId);
    return mapExtent ? [
      Command.CHANGE_CURRENT_EXTENT({
        viewId,
        currentExtent: mapExtent
      })
    ] : [];
  });
  if (commands.length === 0) {
    return Promise.resolve();
  }
  return store.dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })));
};

export const isWorksheetOnDemandOverlayVisible = (state: WorkbookState) =>
  isWorksheetOnDemand(getActiveWorksheet(state)) && isActiveWorksheetDirty(state);

const shouldOnDemandBeConsidered = (state: WorkbookState, command: Command | undefined) => {
  return !isReportMode(state)
    && !isCommandOfType(command, [ Command.SET_SELECTION.type, Command.INVERT_SELECTION.type, Command.ADD_VIEW.type, Command.ADD_NEW_VIEW_WITH_DATA_VIEW.type ])
    && !isCommandOfTypeInViewType(state, command, ViewType.IMAGE, Command.CHANGE_VIEW_SETTINGS.type)
    && !isCommandOfTypeInViewType(state, command, ViewType.TEXT, Command.CHANGE_VIEW_SETTINGS.type)
    && !isHierarchyLevelChangeCommandDueToScaleChange(command)
    && !isSavingMapExtentCompositeCommand(state, command);
};

function isCommandOfType (command: Command | undefined, commandTypes: string[]) {
  return command && commandTypes.includes(command.type);
}

function isCommandOfTypeInViewType (state: WorkbookState, command: Command | undefined, viewType: ViewType, cmdType: string) {
  const viewId = command && (command.payload as { viewId: ViewId })?.viewId;
  if (!viewId) {
    return;
  }
  const view = getViewById(state, viewId);
  return view && view.type === viewType && command.type === cmdType;
}

function isHierarchyLevelChangeCommandDueToScaleChange (command?: Command) {
  return command && command.type === Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION.type
    && (command.payload as SetDataViewAttributeModificationCommandPayload).modificationType === AttributeModificationKey.LOCAL_HIERARCHY_LEVEL;
}

export const getCommandErrorKey = (state: WorkbookState) => slice(state).commandErrorKey;
export const getCommandErrorDetails = (state: WorkbookState) => slice(state).commandErrorDetail;

function isSavingMapExtentCompositeCommand (state: WorkbookState, possibleCompositeCommand?: Command) {
  if (possibleCompositeCommand && possibleCompositeCommand.type === Command.COMPOSITE_COMMAND.type) {
    const simpleCommands = (possibleCompositeCommand.payload as CompositeCommandPayload).commands;
    return simpleCommands.length === 1 && isSavingMapExtentCommand(state, simpleCommands[0]);
  }
  return false;
}

function isSavingMapExtentCommand (state: WorkbookState, command: Command) {
  return isCommandOfTypeInViewType(state, command, ViewType.MAP, Command.CHANGE_CURRENT_EXTENT.type);
}

function isReportMode (state: WorkbookState) {
  return slice(state).reportMode;
}

export function isDataViewOnDemand (dataView: DataViewDto) {
  return getSetting<QueryExecutionStrategyType>(dataView, CommonDataViewSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY')) === 'onDemand';
}

function isActiveWorksheetSettingOnDemand (state: WorkbookState) {
  const activeWorksheet = getActiveWorksheet(state);
  return activeWorksheet && isWorksheetOnDemand(activeWorksheet);
}

function isWorksheetOnDemand (worksheet: WorksheetDto) {
  return getSetting<QueryExecutionStrategyType>(worksheet, WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY')) === 'onDemand';
}

export const getOnDemandLayersIds = (state: WorkbookState, viewId: ViewId): LayerId[] => {
  const view = getViewById(state, viewId);
  if (view.type === ViewType.MAP) {
    return Object.values(view.layerDefinitions)
      .filter(layer =>!isReportMode(state) && layer.settings.dataViewId
        && isDataViewOnDemand(getDataViewById(state, layer.settings.dataViewId as DataViewId)))
      .map(layer => layer.id);
  }
  return [];
};

export const isLayerSettingOnDemand = (state: WorkbookState, viewId: ViewId, layerId: LayerId) => {
  return getOnDemandLayersIds(state, viewId).includes(layerId);
};

/**
 * Should return true only if the user has clicked on the "Update" button to load the on demand layer
 *
 * @param state - The state object
 * @param viewId - The viewId in which the user clicked the button
 * @param layerId - The layer id to check whether it's being loaded
 */
export const isOnDemandLayerLoading = (state: WorkbookState, viewId: ViewId, layerId: LayerId) => {
  const viewOnDemandLayers = slice(state).onDemandLayersLoading[viewId] ?? [];
  return viewOnDemandLayers.includes(layerId);
};

export const isLayerOnDemandAndShouldNotLoad = (
  state: WorkbookState,
  viewId: ViewId,
  layerId: LayerId
) => {
  return isLayerSettingOnDemand(state, viewId, layerId) && !isOnDemandLayerLoading(state, viewId, layerId);
};

export const getAutomaticallyUpdatingLayersIds = (state: WorkbookState, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  if (view.type === ViewType.MAP) {
    return Object.values(view.layerDefinitions)
      .filter(layer =>isReportMode(state) || layer.settings.dataViewId == null
        || !isDataViewOnDemand(getDataViewById(state, layer.settings.dataViewId as DataViewId)))
      .map(layer => layer.id);
  }
  return [];
};

export const layerById = (state: WorkbookState, viewId: ViewId, layerId: LayerId) => {
  const view = getViewById(state, viewId)!;
  return view.layerDefinitions[layerId];
};

export function getLayerName (state: WorkbookState, viewId: ViewId, layerId: LayerId) {
  return getSetting<string>(
    layerById(state, viewId, layerId),
    LayerSettingsKey.toPersistenceKey('LAYER_NAME')
  ) ?? getDataViewById(state, layerId as DataViewId).name;
}

export const getInvalidLayerIdsByViewId = (state: WorkbookState, viewId: ViewId) => {
  return slice(state).invalidLayerIdsByViewId[viewId];
};

export const getLayerData = (state: WorkbookState, viewId: ViewId, layerId: LayerId) => {
  return {
    id: layerId,
    definition: layerById(state, viewId, layerId),
    dataView: getDataViewById(state, toDataViewId(layerId))
  };
};

export const getLastCommand = (state: WorkbookState) => {
  return slice(state).lastCommand;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function slice (state: any): WorkbookState {
  return state[SLICE_PREFIX] ?? state;
}

function createCommandErrorDetails (serverErrorPayload?: Problem) {
  if (serverErrorPayload?.key === WORKBOOK_ERROR_KEYS.NO_SPACE_FOR_VIEW_IN_WORKSHEET_GRID) {
    return { worksheetId: serverErrorPayload.detail };
  }
}

function updateDataViewInvalidationState (
  state: WorkbookState,
  invalidViewsOrDataViews: (ViewId | DataViewId)[],
  invalidLayerIdsByViewId: Record<ViewId, LayerId[]>,
  dataViewsAffectedBySelectionChange: DataViewId[]
) {
  state.invalidViewsOrDataViews = invalidViewsOrDataViews;
  state.invalidLayerIdsByViewId = invalidLayerIdsByViewId;
  state.dataViewsAffectedBySelectionChange = dataViewsAffectedBySelectionChange;

  if (isActiveWorksheetDirty(state) && isActiveWorksheetOnDemand(state)) {
    activateViewInternally(state); // Deactivate active view
  }
}

function activateViewInternally (state: WorkbookState, viewId?: ViewId) {
  if (viewId && isWorksheetOnDemandOverlayVisible(state)) {
    return; // don't activate a view when the worksheet on demand overlay is visible
  }
  state.activeViewId = viewId;
}

function updateActiveLayerId (state: WorkbookState, previousActiveViewState: ViewWithMetadata | undefined, command: Command) {
  const activeView = getActiveView(state);
  if (activeView && previousActiveViewState) {
    const viewChangedToMap = activeView.type === ViewType.MAP && previousActiveViewState.type !== ViewType.MAP;
    if (isCommandType(command, Command.ADD_LAYER_GROUP)) {
      const layerIdsBefore = new Set(getViewLayerIds(previousActiveViewState));
      const layerIdsAfter = getViewLayerIds(activeView);
      state.activeLayerId = layerIdsAfter.find(newId => !layerIdsBefore.has(newId));
    } else if (viewChangedToMap && isCommandType(command, Command.CHANGE_VISUALIZATION)) {
      state.activeLayerId = command.payload.dataViewId;
    } else if (viewChangedToMap && isCommandType(command, Command.ADD_ATTRIBUTE)) {
      state.activeLayerId = activeView.dataViews.at(-1);
    }
  }
  const activeLayerId = state.activeLayerId;
  if (activeLayerId && !Object.values(state.viewById).some((view) => view.layerDefinitions?.[activeLayerId] != null)) {
    delete state.activeLayerId;
  }
}

function unsetMaximizedViewIfViewDoesNotExist (state: WorkbookState, command: Command) {
  if (isCommandType(command, Command.REMOVE_VIEW)) {
    const maximizedViewId = getMaximizedViewId(state);
    if (maximizedViewId && getViewById(state, maximizedViewId) == null) {
      state.maximizedViewId = '';
    }
  }
}

function getViewLayerIds (view: ViewWithMetadata) {
  return Object.keys(view.layerDefinitions ?? {}) as LayerId[];
}

function getInitialMaximizedViewIdFromUrlParam (parameterName: string) {
  return getUrlParam({ name: parameterName }) as ViewId || '';
}

export function canManageObjecttype (objecttypeId: ObjecttypeIdString) {
  const objecttype = getObjecttypeById(store.getState(), objecttypeId);
  return objecttype != null
    && isFeatureAvailable('ADMINISTRATION')
    && isWorkbookVisible(store.getState())
    && hasPrivilege(objecttype.privileges, 'visible')
    && !isObjecttypeBound(objecttype);
}

export const canCreateMapContext = createSelector(
  [ getActiveOrFirstMapViewId, isAnyDefaultMapDefined ],
  (viewId, anyDefaultMapDefined) => viewId !== undefined || anyDefaultMapDefined
);

// Client-side state persistence

store.subscribe(() => {
  const state = slice(store.getState());
  persistPanelState(state);
  // state.workbookIdAndHash is always here on an open workbook, but the '?' is needed for some tests
  // that trigger this without initializing the workbook store first
  cadenzaSessionStorage.setItem(
    WORKBOOK_CHANGED_KEY,
    state.clientChanged ? state.workbookIdAndHash?.id : ''
  );
});

observeStore(store, getWorkbookIdAndHash, (workbookIdAndHash) => updateWorkbookUrl(workbookIdAndHash, true));
